import {
  Component,
  createSignal,
  Show,
  createEffect,
  onCleanup,
  onMount,
  createMemo,
} from 'solid-js';
import { ColorPicker } from '@trimble/ui-components/solidjs';
import { Portal } from 'solid-js/web';
import { RgbColor } from 'solid-color';
import { t } from '@trimble/i18n';
import { CustomInput, Button } from '@tcweb/connect-ui';

interface EditPointMeasurementProps {
  editPointModalRef(el: HTMLModusModalElement): void;
  pointLabel?: string;
  pointColor: RgbColor;
  getRGBColorString(color: RgbColor): string;
  onClose(): void;
  onSave(label: string, color: RgbColor): void;
}

export const EditPointMeasurement: Component<EditPointMeasurementProps> = (
  props
) => {
  const [pointLabel, setPointLabel] = createSignal<string>('');
  const [showColorPicker, setShowColorPicker] = createSignal<boolean>(false);
  const defaultColor: RgbColor = { r: 236, g: 0, b: 182 }; // Default color #EC00B6
  const [editedColor, setEditedColor] = createSignal<RgbColor>(defaultColor);
  let colorBtnRef: HTMLButtonElement | undefined;
  const [resized, setResized] = createSignal(true, { equals: false });

  // Sync editedColor and pointLabel with props
  // TODO: Refactor to remove createEffect to set signals
  createEffect(() => {
    setEditedColor(props.pointColor);
    setPointLabel(props.pointLabel ? props.pointLabel : '');
  });

  const handlePointLabelChange = (
    e: Event & { currentTarget: HTMLInputElement; target: Element }
  ): void => {
    setPointLabel(e.currentTarget.value);
  };

  const handleColorSave = (color: RgbColor): void => {
    setEditedColor(color);
    setShowColorPicker(false);
  };

  const buttonPosition = createMemo(() => {
    if (resized() && showColorPicker() && colorBtnRef) {
      const rect = colorBtnRef.getBoundingClientRect();
      return { left: rect.left, top: rect.top, width: rect.width };
    }
    return { left: 0, top: 0, width: 0 };
  });

  onMount(() => {
    window.addEventListener('resize', setResized);
  });

  onCleanup(() => {
    window.removeEventListener('resize', setResized);
  });

  const handleEditModalCancel = (): void => {
    setPointLabel(props.pointLabel ? props.pointLabel : '');
    setEditedColor(props.pointColor);
    setShowColorPicker(false);
    props.onClose();
  };

  const handleEditModalSave = (): void => {
    props.onSave(pointLabel(), editedColor());
    handleEditModalCancel();
  };

  return (
    // eslint-disable-next-line custom/no-modus-v1 -- TWENG-132
    <modus-modal
      id="edit-point-measurement-modal"
      header-text={t('web3D.toolbar.actions.editPointMeasurement')}
      // @ts-expect-error Property 'ref' does not exist on type ModusModal
      ref={(el) => {
        props.editPointModalRef(el as HTMLModusModalElement);
      }}
      on:closed={handleEditModalCancel}
    >
      <div>
        <div>
          <CustomInput
            name="edit-point-label"
            label="Point Label"
            placeholder="Enter point label"
            class="w-80 pt-2"
            onChange={handlePointLabelChange}
            value={pointLabel()}
          />
        </div>
        <div>
          <div class="flex items-center gap-2 mt-5 relative">
            <div class="text-[0.75rem] font-bold text-gray-8 mr-3">
              Edit Color
            </div>
            <button
              ref={colorBtnRef}
              class="w-6 h-6 rounded-full border border-gray-300 cursor-pointer"
              style={{
                ['background-color']: props.getRGBColorString(editedColor()),
              }}
              onClick={() => setShowColorPicker(!showColorPicker())}
            />
            <Show when={showColorPicker()}>
              <Portal>
                <div
                  class="absolute z-dropdown"
                  style={{
                    // eslint-disable-next-line no-magic-numbers
                    left: `${String(buttonPosition().left + buttonPosition().width + 8)}px`,
                    // eslint-disable-next-line no-magic-numbers
                    top: `${String(buttonPosition().top + 300)}px`,
                  }}
                >
                  <ColorPicker
                    initialColor={props.getRGBColorString(editedColor())}
                    onSave={handleColorSave}
                  />
                </div>
              </Portal>
            </Show>
          </div>
        </div>
        <div class="flex justify-end pt-8 gap-2">
          <Button
            class="w-30"
            data-cy="btn-cancel"
            type="outline"
            onClick={handleEditModalCancel}
          >
            {t('general.cancel')}
          </Button>
          <Button class="w-30" data-cy="btn-save" onClick={handleEditModalSave}>
            {t('general.save')}
          </Button>
        </div>
      </div>
    </modus-modal>
  );
};
